/* Enterprise rendering adapter. FeeBee remains the only owner of fee mutations.
 * Depends on the checked FeeBee feeManager/cartUIManager interface (feebee-150).
 * No prices, Markets, discounts or deposit quantities are calculated here.
 */
(function (root) {
  function createCollectionFallback({ fetch, window, getConfig }) {
    const cache = new Map();
    const shortId = (id) => String(id).split('/').pop();
    async function memberships(productId) {
      const country = window.Shopify?.country || 'DE';
      const key = `${country}:${productId}`;
      if (!cache.has(key)) {
        const request = (async () => {
          const ids = new Set();
          let cursor = null;
          do {
            const response = await fetch('/api/2025-10/graphql.json', {
              method: 'POST', credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                query: 'query FeeCollections($id: ID!, $after: String, $country: CountryCode!) @inContext(country: $country) { product(id: $id) { collections(first: 100, after: $after) { nodes { id } pageInfo { hasNextPage endCursor } } } }',
                variables: { id: `gid://shopify/Product/${productId}`, after: cursor, country }
              })
            });
            if (!response.ok) throw new Error(`Fee collection lookup failed: ${response.status}`);
            const result = await response.json();
            const connection = result.data?.product?.collections;
            if (result.errors?.length || !connection) throw new Error('Fee collection lookup returned incomplete data');
            connection.nodes.forEach((node) => ids.add(shortId(node.id)));
            const next = connection.pageInfo.hasNextPage ? connection.pageInfo.endCursor : null;
            if (connection.pageInfo.hasNextPage && (!next || next === cursor)) throw new Error('Fee collection pagination did not advance');
            cursor = next;
          } while (cursor);
          return ids;
        })();
        cache.set(key, request);
        request.catch(() => cache.delete(key));
      }
      return cache.get(key);
    }
    return async function needsParent(cart) {
      const config = getConfig();
      const feeCollections = new Set();
      for (const fee of config.globalFees || []) {
        if (fee.feeType !== 'COLLECTIONS') continue;
        if (!fee.appliesToAllMarkets && !(fee.appliesToMarkets || []).includes(config.currentMarketId)) continue;
        const matchesRules = (fee.rules || []).every((rule) => {
          if (rule.type !== 'customer_tag') return false;
          const match = (config.customerTagChecks || []).find((item) => String(item.tag) === String(rule.value));
          const hasTag = match?.hasTag === true;
          return rule.operator === 'includes' ? hasTag : rule.operator === 'excludes' ? !hasTag : false;
        });
        if (matchesRules) (fee.appliesToCollections || []).forEach((id) => feeCollections.add(shortId(id)));
      }
      if (!feeCollections.size) return false;
      const known = new Map((config.productCollectionsIds || []).map((entry) => {
        const [id, values] = String(entry).split(':');
        return [id, (values || '').split(',').filter(Boolean)];
      }));
      if (config.currentProductId && !known.has(String(config.currentProductId))) {
        known.set(String(config.currentProductId), config.currentProductCollectionIds || []);
      }
      for (const item of cart.items || []) {
        if (shortId(item.variant_id) === shortId(config.globalFeeProductVariantId)) continue;
        const productId = shortId(item.product_id);
        const liquidIds = known.get(productId);
        // Shopify's Liquid product.collections can omit memberships beyond its first 50.
        // Complete known lists need no additional request; unknown cart products do.
        if (liquidIds && liquidIds.length < 50) continue;
        const ids = await memberships(productId);
        if ([...feeCollections].some((id) => ids.has(id))) return true;
      }
      return false;
    };
  }
  function isCartWriteResource(name, origin) {
    try {
      const url = new URL(name, origin);
      return url.origin === origin && /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname);
    } catch (_) {
      return false;
    }
  }
  function createBridge(env) {
    const { document, fetch, window } = env;
    let manager;
    let revision = 0;
    let pending = false;
    let running = null;
    let openRequested = false;
    let contextNavigation = false;
    const recovery = createRecovery({
      online: () => window.navigator?.onLine !== false,
      sync: () => refresh(openRequested),
      display: createRecoveryNotice(document)
    });

    function requiresContextReload(productId) {
      if (!productId) return false;
      try {
        const config = JSON.parse(document.getElementById('feebee-config')?.textContent || '{}');
        if (!config.globalFees?.some((fee) => fee.feeType === 'COLLECTIONS')) return false;
        const known = new Set((config.productCollectionsIds || []).map((entry) => String(entry).split(':')[0]));
        if (config.currentProductId) known.add(String(config.currentProductId));
        return !known.has(String(productId));
      } catch (_) { return false; }
    }

    function reloadContext() {
      if (contextNavigation) return;
      contextNavigation = true;
      // FeeBee captures its collection registry at bootstrap and exposes no reload API.
      // A new cart document supplies all cart products to the app's own evaluator.
      const url = new URL(`${window.Shopify?.routes?.root || '/'}cart`, window.location.href);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      window.location.assign(url.toString());
    }

    function isActive() {
      const candidate = window.feeManager;
      if (candidate === manager && manager) return true;
      const ui = candidate?.cartUIManager;
      if (!candidate || typeof candidate.sync !== 'function'
        || typeof ui?.reloadCartIfNeeded !== 'function'
        || typeof ui?.applyFeeUIChanges !== 'function') return false;

      manager = candidate;
      if (typeof manager.cfg?.shouldApplyFee === 'function') {
        const originalShouldApplyFee = manager.cfg.shouldApplyFee.bind(manager.cfg);
        const fallback = createCollectionFallback({ fetch, window, getConfig: () => {
          try { return JSON.parse(document.getElementById('feebee-config')?.textContent || '{}'); }
          catch (_) { return {}; }
        } });
        manager.cfg.shouldApplyFee = async (cart) => (await originalShouldApplyFee(cart)) || fallback(cart);
      }
      // Use Enterprise's row and control selectors instead of the Dawn fallback.
      ui.lineTpl = null;
      ui.variantTpl = '[data-variant-id="${variant}"]';
      ui.rowSel = '.cart-item';
      ui.hide = ['.cart-item__remove', '.qty-input__btn'];
      ui.disable = ['.qty-input__input', '.qty-input__btn'];
      const applyFeeUIChanges = ui.applyFeeUIChanges.bind(ui);
      ui.applyFeeUIChanges = (cart, variantId) => {
        // FeeBee's MutationObserver needs the latest cart after section replacement.
        ui.cachedCart = cart;
        return applyFeeUIChanges(cart, variantId);
      };
      ui.reloadCartIfNeeded = () => {
        refresh().catch(reportError);
      };
      return true;
    }

    function reportError(error) {
      console.error('[FeeBee Enterprise] Cart refresh failed', error);
      if (openRequested || manager?.cartUIManager.cachedCart?.items?.length) recovery.failed();
    }

    function invalidate() {
      revision += 1;
    }

    async function render(version) {
      // FeeBee may already be adding the parent fee in response to a product add.
      await manager.inFlight;
      await manager.sync('enterprise-render');
      await manager.inFlight;
      if (version !== revision) return;
      const cart = manager.cartUIManager.cachedCart;
      // A fulfilled app sync is not necessarily a successful fee addition.
      if (cart && typeof manager.cfg?.shouldApplyFee === 'function'
        && await manager.cfg.shouldApplyFee(cart)
        && !cart.items.some((item) => String(item.variant_id) === String(manager.feeVariantId).split('/').pop())) {
        throw new Error('Required fee is still missing after synchronization');
      }
      if (version !== revision) return;
      if (cart?.items?.some((item) => String(item.variant_id) !== String(manager.feeVariantId)
        && requiresContextReload(item.product_id))) {
        reloadContext();
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      const items = document.querySelector('cart-items');
      if (!drawer && !items) return;
      const sections = drawer ? drawer.getSectionsToRender() : items.getSectionsToRender();
      const url = new URL(window.location.href);
      url.searchParams.set('sections', [...new Set(sections.map((s) => s.section))].join(','));
      // Keep preview_theme_id / Market context and cookies on the current URL.
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const html = await response.json();
      if (version !== revision) return;
      if (sections.some((s) => typeof html[s.section] !== 'string')) {
        throw new Error('Incomplete cart sections; retaining the existing cart');
      }
      if (drawer) {
        await drawer.renderContents({ sections: html }, false, () => version === revision);
      } else {
        for (const section of sections) {
          const container = document.getElementById(section.id);
          if (!container) continue;
          const target = container.querySelector(section.selector) || container;
          target.innerHTML = items.constructor.getElementHTML(html[section.section], section.selector);
        }
      }
      if (version !== revision) return;
      const currentItems = document.querySelector('cart-items');
      if (currentItems) {
        currentItems.currentQuantities = [];
        currentItems.currentTotalItemCount = 0;
        currentItems.querySelectorAll('.cart-item').forEach((row) => {
          const quantity = Number(row.querySelector('.qty-input__input')?.value || 0);
          currentItems.currentQuantities[row.dataset.variantId] = quantity;
          currentItems.currentTotalItemCount += quantity;
        });
        currentItems.dataset.empty = String(currentItems.currentTotalItemCount === 0);
      }
      if (manager.cartUIManager.cachedCart) {
        manager.cartUIManager.applyFeeUIChanges(manager.cartUIManager.cachedCart, manager.feeVariantId);
      }
      if (drawer && openRequested && drawer.getAttribute('open') === null) drawer.open();
      openRequested = false;
      recovery.succeeded();
    }

    function refresh(open = false) {
      if (!isActive()) return Promise.resolve();
      revision += 1;
      pending = true;
      openRequested = openRequested || open;
      if (!running) {
        // Defer so callers can register the promise before the first render starts.
        running = Promise.resolve().then(async () => {
          while (pending) {
            pending = false;
            const version = revision;
            try {
              await render(version);
            } catch (error) {
              if (!pending) throw error;
            }
          }
        }).finally(() => { running = null; });
      }
      return running;
    }

    function connectionLost() {
      if (isActive() && (running || openRequested || manager.cartUIManager.cachedCart?.items?.length)) recovery.failed();
    }
    return { isActive, invalidate, refresh, reportError, requiresContextReload, reloadContext,
      connectionLost, connectionRestored: recovery.retry };
  }

  function createRecovery({ online, sync, display }) {
    let affected = false;
    let busy = null;
    function failed() { affected = true; display(online() ? 'error' : 'offline', retry); }
    function succeeded() { affected = false; display(null, retry); }
    function retry() {
      if (!affected) return Promise.resolve();
      if (!online()) { failed(); return Promise.resolve(); }
      if (busy) return busy;
      display('checking', retry);
      busy = Promise.resolve().then(sync).catch(failed).finally(() => { busy = null; });
      return busy;
    }
    return { failed, succeeded, retry };
  }

  function createRecoveryNotice(document) {
    if (!document.createElement) return () => {};
    const messages = {
      de: ['Verbindung unterbrochen. Ihr Warenkorb wird geprüft, sobald Sie wieder online sind.', 'Ihr Warenkorb wird aktualisiert…', 'Ihr Warenkorb konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.', 'Erneut versuchen'],
      fr: ['Connexion interrompue. Votre panier sera vérifié au retour de la connexion.', 'Mise à jour de votre panier…', 'Votre panier n’a pas pu être mis à jour. Veuillez réessayer.', 'Réessayer'],
      en: ['Connection lost. We’ll check your cart when you’re back online.', 'Updating your cart…', 'Your cart could not be updated. Please try again.', 'Try again'],
      es: ['Conexión interrumpida. Revisaremos tu carrito cuando vuelva la conexión.', 'Actualizando tu carrito…', 'No se pudo actualizar tu carrito. Inténtalo de nuevo.', 'Reintentar'],
      it: ['Connessione interrotta. Controlleremo il carrello al ripristino della connessione.', 'Aggiornamento del carrello…', 'Impossibile aggiornare il carrello. Riprova.', 'Riprova'],
      tr: ['Bağlantı kesildi. İnternet geri geldiğinde sepetiniz kontrol edilecek.', 'Sepetiniz güncelleniyor…', 'Sepetiniz güncellenemedi. Lütfen tekrar deneyin.', 'Tekrar dene']
    };
    let notice;
    return (state, retry) => {
      if (!state) { notice?.remove(); notice = null; return; }
      if (!document.getElementById('dfn-cart-recovery-style')) {
        const style = document.createElement('style');
        style.id = 'dfn-cart-recovery-style';
        style.textContent = '.dfn-cart-recovery{display:flex;align-items:flex-start;gap:9px;margin:12px 0;padding:11px 13px;border:1px solid #dbcba7;border-radius:8px;background:#faf7ef;color:#514733;font-family:inherit;font-size:13px;line-height:1.5;text-align:start}.dfn-cart-recovery__icon{flex:none;font-size:16px;line-height:1.3}.dfn-cart-recovery__retry{display:block;margin-top:5px;padding:2px 0;border:0;background:none;color:inherit;font:inherit;font-weight:600;text-decoration:underline;cursor:pointer}.dfn-cart-recovery__retry[hidden]{display:none}.dfn-cart-recovery__retry:focus-visible{outline:2px solid currentColor;outline-offset:3px}';
        document.head.append(style);
      }
      if (!notice) {
        notice = document.createElement('div');
        notice.className = 'dfn-cart-recovery';
        notice.innerHTML = '<span class="dfn-cart-recovery__icon" aria-hidden="true">ⓘ</span><div><span role="status" aria-live="polite" aria-atomic="true"></span><button type="button" class="dfn-cart-recovery__retry"></button></div>';
      }
      const lang = (document.documentElement.lang || 'en').toLowerCase().split('-')[0];
      const copy = messages[lang] || messages.en;
      notice.dataset.state = state;
      notice.querySelector('[role="status"]').textContent = copy[state === 'offline' ? 0 : state === 'checking' ? 1 : 2];
      const button = notice.querySelector('button');
      button.textContent = copy[3]; button.hidden = state !== 'error'; button.onclick = retry;
      const drawer = document.querySelector('cart-drawer[open]');
      const checkout = drawer?.querySelector('[name="checkout"]') || document.querySelector('main [name="checkout"]');
      const product = document.querySelector('main product-form');
      // Keep feedback visible even when the failed add never opened the drawer.
      if (checkout) checkout.before(notice);
      else if (product) product.append(notice);
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createBridge, isCartWriteResource, createCollectionFallback, createRecovery };
    return;
  }
  if (root.dfnFeeBeeEnterprise) return;
  const bridge = createBridge({ window: root, document: root.document, fetch: root.fetch.bind(root) });
  root.dfnFeeBeeEnterprise = bridge;
  root.addEventListener('offline', bridge.connectionLost);
  root.addEventListener('online', bridge.connectionRestored);
  // Observe completed Ajax cart writes from apps too; never intercept their requests.
  // Section and cart reads are excluded, so our own rendering cannot trigger a loop.
  if (typeof root.PerformanceObserver === 'function') {
    let refreshTimer;
    const observer = new root.PerformanceObserver((list) => {
      if (!list.getEntries().some((entry) => isCartWriteResource(entry.name, root.location.origin))) return;
      if (!bridge.isActive()) return;
      bridge.invalidate();
      root.clearTimeout(refreshTimer);
      refreshTimer = root.setTimeout(() => bridge.refresh().catch(bridge.reportError), 80);
    });
    try {
      observer.observe({ entryTypes: ['resource'] });
    } catch (error) {
      console.warn('[FeeBee Enterprise] Resource observation unavailable', error);
    }
  }
  root.addEventListener('pageshow', (event) => {
    if (event.persisted && bridge.isActive()) bridge.refresh().catch(bridge.reportError);
  });
  // FeeBee can bootstrap after this deferred theme asset. Stop polling once attached.
  let attempts = 0;
  const timer = root.setInterval(() => {
    if (bridge.isActive()) {
      root.clearInterval(timer);
      bridge.refresh().catch(bridge.reportError);
    } else if (++attempts >= 120) {
      root.clearInterval(timer);
    }
  }, 250);
})(typeof window === 'undefined' ? globalThis : window);
